import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

export type MoneyhubAccount = {
  id: string;
  moneyhub_id: string;
  name: string | null;
  type: string;
  asset_category: string;
  provider_name: string | null;
  holder_name: string | null;
  currency: string | null;
  status: string;
  current_balance_minor: number | null;
};

type MoneyhubTransaction = {
  id: string;
  moneyhub_id: string;
  amount_minor: number;
  currency: string | null;
  booking_date: string;
  description: string | null;
  merchant_name: string | null;
  category_id: string | null;
  status: string | null;
};

type StartConnectionResponse = {
  auth_url: string;
};

type CompleteConnectionResponse = {
  connection_id: string;
  moneyhub_connection_id: string;
};

const MONEYHUB_ACCOUNTS_KEY = ['moneyhub', 'accounts'];

export const useMoneyhubAccounts = () => {
  return useQuery<MoneyhubAccount[]>({
    queryKey: MONEYHUB_ACCOUNTS_KEY,
  });
};

export const useMoneyhubTransactions = (accountId: string | null) => {
  return useQuery<MoneyhubTransaction[]>({
    queryKey: ['moneyhub', 'accounts', accountId, 'transactions'],
    enabled: !!accountId,
  });
};

export const useStartMoneyhubConnection = () => {
  const { mutateAsync, isPending, error } = useMutation<
    { data: StartConnectionResponse },
    Error,
    { bank_id: string | null }
  >({
    mutationFn: body => api.post<StartConnectionResponse>('moneyhub/connections/start', body),
  });

  return {
    startConnection: mutateAsync,
    isStarting: isPending,
    startError: error,
  };
};

export const useCompleteMoneyhubConnection = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<
    { data: CompleteConnectionResponse },
    Error,
    { code: string; state: string }
  >({
    mutationFn: body => api.post<CompleteConnectionResponse>('moneyhub/connections/callback', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MONEYHUB_ACCOUNTS_KEY });
    },
  });

  return {
    completeConnection: mutateAsync,
    isCompleting: isPending,
    completeError: error,
  };
};
